import React from "react";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { connectPageService } from "../../../../application/domain/base/base-application-service";
import { ServiceProviderTemplate } from "../../../template/service-provider/default-template";
import { ServiceProviderTabs } from "../../../molecules/service-provider-tab";
import { ServiceProviderR } from "../routes";
import {
  OrgProductsService,
  OrgProductsContextProps,
} from "../../../../application/service/service-provider/org-products-service";
import { useModal } from "../../../../utils/custom-hooks";
import { OrgProductCreateModal } from "../../../organisms/service-provider/modal/org-product-create-modal";
import { OrgProductUpdateModal } from "../../../organisms/service-provider/modal/org-product-update-modal";
import {
  OrgProductType,
  ProductCodes,
  ProductNameMap,
  ProductCodeTypeLiterals,
} from "../../../../application/domain/organization/org-product";
import {
  PrimaryButton,
  SecondaryButton,
} from "@graffer-inc/console-design-kit";
import { StaticFeatureFlags } from "../../../../feature-flags";
import { GrafferGqlResult } from "@graffer-inc/smart-apply";

export const OrgProductsPage: React.FC<OrgProductsContextProps> = ({
  orgProducts,
  procedurePublishingCount,
  applicationCount,
  surveyPublishingCount,
  surveyAnswerCount,
  fetchOrgProduct,
  updateOrgProduct,
  createOrgProduct,
  fetchTenantDataCount,
}) => {
  const { id: orgId } = useParams<{ id: string }>();
  const createModal = useModal();
  const updateModal = useModal();
  const [selectedOrgProduct, setSelectedOrgProduct] =
    React.useState<OrgProductType>();

  const productCodes = useMemo(
    () => orgProducts.map((v) => v.productCode) ?? [],
    [orgProducts],
  );
  const createProductCodeOptions = useMemo(
    () =>
      ProductCodes.filter(
        (code) =>
          code !== "KintoneExport" || StaticFeatureFlags.ENABLE_KINTONE_EXPORT,
      ).reduce<ProductCodeTypeLiterals[]>((acc, cur) => {
        if (!productCodes.includes(cur)) {
          acc.push(cur);
        }
        return acc;
      }, []),
    [productCodes],
  );

  useEffect(() => {
    fetchOrgProduct(orgId);
    fetchTenantDataCount(orgId);
  }, [orgId, fetchOrgProduct, fetchTenantDataCount]);

  type ProductDataCount = {
    productName: string;
    metrics: {
      title: string;
      gqlResult: GrafferGqlResult<number>;
      rootPath: Parameters<
        GrafferGqlResult<number>["matchResult"]
      >[0]["rootPath"];
    }[];
  };
  const productDataCounts = useMemo<ProductDataCount[]>(
    () => [
      {
        productName: "スマート申請",
        metrics: [
          {
            title: "公開済み手続き数",
            gqlResult: procedurePublishingCount,
            rootPath: "publishingProcedureCount",
          },
          {
            title: "申請数",
            gqlResult: applicationCount,
            rootPath: "applicationCountByOrgId",
          },
        ],
      },
      {
        productName: "アンケート",
        metrics: [
          {
            title: "公開済みアンケート数",
            gqlResult: surveyPublishingCount,
            rootPath: "publishingSurveyCount",
          },
          {
            title: "アンケート回答数",
            gqlResult: surveyAnswerCount,
            rootPath: "surveyAnswerCountByOrgId",
          },
        ],
      },
    ],
    [
      procedurePublishingCount,
      applicationCount,
      surveyPublishingCount,
      surveyAnswerCount,
    ],
  );
  return (
    <ServiceProviderTemplate
      breadcrumbs={[{ to: ServiceProviderR.Home.link({}), title: "Home" }]}
    >
      <ServiceProviderTabs orgId={orgId} />
      <h3
        className="title is-size-4 mt-6 mb-4 pl-2"
        style={{
          borderLeft: "6px solid #40bfb0",
        }}
      >
        製品プラン設定
      </h3>
      <nav className="level">
        <div className="level-left" />
        <div className="level-right">
          <p className="level-item">
            {ProductCodes.some((v) => !productCodes.includes(v)) && (
              <PrimaryButton
                text={"製品プランを追加"}
                onClick={createModal.open}
              />
            )}
          </p>
        </div>
      </nav>
      <table className="table is-fullwidth">
        <thead>
          <tr>
            <th>製品名</th>
            <th>製品プラン</th>
            <th>利用規約URL</th>
            <th aria-label="編集"></th>
          </tr>
        </thead>
        <tbody>
          {orgProducts.map((p) => (
            <tr key={p.id}>
              <td>{p.productName}</td>
              <td>{p.planName}</td>
              <td>
                <a
                  href={p.legalTermUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {p.legalTermUrl}
                </a>
              </td>
              <td>
                <SecondaryButton
                  text="編集"
                  onClick={() => {
                    setSelectedOrgProduct(p);
                    updateModal.open();
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {createModal.isActive && (
        <OrgProductCreateModal
          orgId={orgId}
          isActive={createModal.isActive}
          onSubmit={async (orgProduct) => {
            const isCreated = await createOrgProduct(orgProduct);
            if (isCreated) {
              toast.success(
                `${
                  ProductNameMap[orgProduct.productCode]
                }の製品プランを設定しました`,
              );
              createModal.close();
              fetchOrgProduct(orgId);
              return true;
            }
            return false;
          }}
          onClose={createModal.close}
          productCodeOptions={createProductCodeOptions}
        />
      )}
      {updateModal.isActive && selectedOrgProduct && (
        <OrgProductUpdateModal
          orgProduct={selectedOrgProduct}
          isActive={updateModal.isActive}
          onSubmit={async (orgProduct) => {
            const isUpdated = await updateOrgProduct(orgProduct);
            if (isUpdated) {
              toast.success(
                `${
                  ProductNameMap[orgProduct.productCode]
                }の製品プランを変更しました`,
              );
              updateModal.close();
              fetchOrgProduct(orgId);
            }
          }}
          onCancel={updateModal.close}
        />
      )}

      {productDataCounts.map((p, index) => (
        <div key={index}>
          <h3
            className="title is-size-4 mt-6 mb-4 pl-2"
            style={{
              borderLeft: "6px solid #40bfb0",
            }}
          >
            {p.productName}
          </h3>
          <div className="columns">
            {p.metrics.map((m, j) => (
              <div className="column is-4" key={j}>
                <div className="card">
                  <div className="card-content">
                    <p className="title is-size-6">{m.title}</p>
                    <p className="is-size-4 has-text-centered px-4 py-4">
                      {m.gqlResult.matchResult(
                        {
                          rootPath: m.rootPath,
                          errorTypes: "",
                        },
                        {
                          loading: "読込中です...",
                          success: (count: number): React.ReactNode => {
                            return count.toString(10);
                          },
                          unexpected: "予期せぬエラーが発生しました",
                        },
                      )}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </ServiceProviderTemplate>
  );
};

export const OrgProductsContainer = connectPageService(
  OrgProductsPage,
  () => new OrgProductsService(),
);
